import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Search, ChevronRight, ChevronDown, BookOpen, FileText,
  ThumbsUp, ThumbsDown, Play, CheckCircle2, ArrowLeft, Loader2
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = { 'Getting Started': '01', 'Business Setup': '02', 'Workflows': '03', 'Operations': '04', 'Tools': '05', 'Knowledge': '06', 'Client Management': '07', 'Growth': '08' };

// ==================== HELP HOME ====================
export const HelpCenter = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [artRes, catRes] = await Promise.all([
        axios.get(`${API}/help/articles`),
        axios.get(`${API}/help/categories`),
      ]);
      setArticles(artRes.data);
      setCategories(catRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!search.trim()) return fetchData();
    try {
      const res = await axios.get(`${API}/help/articles?search=${encodeURIComponent(search)}`);
      setArticles(res.data);
    } catch {}
  };

  const grouped = {};
  articles.forEach(a => { if (!grouped[a.category]) grouped[a.category] = []; grouped[a.category].push(a); });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="noise-overlay" />
      <nav className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2"><HSLogo size={28} /><span className="font-black text-base">HumanScaled</span></Link>
          <span className="text-xs text-muted-foreground font-mono">HELP CENTER</span>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div className="text-center space-y-3">
          <BookOpen className="w-10 h-10 mx-auto text-emerald-400" />
          <h1 className="text-3xl font-bold">How can we help?</h1>
          <div className="flex max-w-lg mx-auto gap-2">
            <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search articles..." className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="help-search" />
            <Button onClick={handleSearch} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm"><Search className="w-4 h-4" /></Button>
          </div>
        </div>

        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div> : (
          <div className="space-y-8">
            {/* Category cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {categories.map(cat => (
                <div key={cat.category} className="bg-[#0a0a0a] border border-white/5 p-4 hover:border-emerald-500/20 transition-colors cursor-pointer" onClick={() => {
                  document.getElementById(`cat-${cat.category}`)?.scrollIntoView({ behavior: 'smooth' });
                }}>
                  <span className="text-xs font-mono text-emerald-400 mb-1 block">{CATEGORY_ICONS[cat.category] || '00'}</span>
                  <h3 className="font-bold text-sm">{cat.category}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{cat.count} article{cat.count > 1 ? 's' : ''}</p>
                </div>
              ))}
            </div>

            {/* Articles by category */}
            {Object.entries(grouped).map(([category, arts]) => (
              <div key={category} id={`cat-${category}`}>
                <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">{category}</h2>
                <div className="space-y-1">
                  {arts.map(a => (
                    <Link key={a.slug} to={`/help/${a.slug}`} className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-white/5 hover:border-emerald-500/20 transition-colors group" data-testid={`article-${a.slug}`}>
                      <div>
                        <p className="font-medium text-sm group-hover:text-emerald-400 transition-colors">{a.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{a.summary.slice(0, 80)}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ARTICLE VIEW ====================
export const HelpArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guideActive, setGuideActive] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState({});
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [comment, setComment] = useState('');

  useEffect(() => { fetchArticle(); }, [slug]);

  const fetchArticle = async () => {
    try { const r = await axios.get(`${API}/help/articles/${slug}`); setArticle(r.data); }
    catch { navigate('/help'); }
    finally { setLoading(false); }
  };

  const toggleStep = (i) => setCheckedSteps(prev => ({ ...prev, [i]: !prev[i] }));

  const handleFeedback = async (helpful) => {
    try {
      await axios.post(`${API}/help/feedback`, { article_slug: slug, helpful, comment });
      toast.success('Feedback submitted');
      setFeedbackGiven(true);
    } catch { toast.error('Failed'); }
  };

  const handleGuideMe = () => {
    setGuideActive(true);
    setCheckedSteps({});
    // The context payload that would be passed to an assistant
    const contextPayload = {
      mode: "guided",
      article_slug: slug,
      route: window.location.pathname,
    };
    toast.info(`Guide mode activated for: ${article?.title}`);
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  if (!article) return null;

  const allChecked = article.steps_json?.length > 0 && Object.keys(checkedSteps).length === article.steps_json.length && Object.values(checkedSteps).every(Boolean);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="noise-overlay" />
      <nav className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/help" className="text-muted-foreground hover:text-white"><ArrowLeft className="w-4 h-4" /></Link>
            <Link to="/dashboard" className="flex items-center gap-2"><HSLogo size={24} /><span className="font-black text-sm">HumanScaled</span></Link>
          </div>
          <span className="text-xs text-muted-foreground font-mono">{article.category}</span>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {article.tags?.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground">{t}</span>)}
          </div>
          <h1 className="text-3xl font-bold mb-3" data-testid="article-title">{article.title}</h1>
          <p className="text-muted-foreground">{article.summary}</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-sm max-w-none" data-testid="article-content">
          {article.content_markdown?.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('## ', '')}</h2>;
            if (line.startsWith('### ')) return <h3 key={i} className="text-base font-bold mt-4 mb-2">{line.replace('### ', '')}</h3>;
            if (line.startsWith('- **')) {
              const parts = line.replace('- **', '').split('**');
              return <p key={i} className="text-sm text-muted-foreground ml-4 mb-1"><span className="text-white font-medium">{parts[0]}</span>{parts[1] || ''}</p>;
            }
            if (line.startsWith('- ')) return <p key={i} className="text-sm text-muted-foreground ml-4 mb-1">• {line.replace('- ', '')}</p>;
            if (line.trim()) return <p key={i} className="text-sm text-muted-foreground mb-2">{line}</p>;
            return null;
          })}
        </div>

        {/* Steps + Guide Me */}
        {article.steps_json?.length > 0 && (
          <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="steps-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Step-by-Step Guide</h3>
              <Button onClick={handleGuideMe} className={`rounded-sm h-8 text-xs ${guideActive ? 'bg-emerald-500 text-black' : 'bg-white/5 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10'}`} data-testid="guide-me-btn">
                <Play className="w-3 h-3 mr-1" /> {guideActive ? 'Guide Active' : 'Guide Me'}
              </Button>
            </div>
            <div className="space-y-2">
              {article.steps_json.map((step, i) => (
                <button key={i} onClick={() => guideActive && toggleStep(i)} className={`w-full flex items-start gap-3 p-3 text-left transition-colors ${guideActive ? 'cursor-pointer hover:bg-white/5' : ''} ${checkedSteps[i] ? 'bg-emerald-500/5 border border-emerald-500/20' : 'border border-white/5'}`} data-testid={`step-${i}`}>
                  <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${checkedSteps[i] ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                    {checkedSteps[i] ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-xs font-mono">{step.step}</span>}
                  </div>
                  <div>
                    <p className={`text-sm ${checkedSteps[i] ? 'text-emerald-400 line-through' : ''}`}>{step.action}</p>
                    {step.route && <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{step.route}</p>}
                  </div>
                </button>
              ))}
            </div>
            {allChecked && <p className="text-emerald-400 text-sm font-medium text-center mt-4">All steps completed!</p>}
          </div>
        )}

        {/* Feedback */}
        <div className="border border-white/5 p-5 text-center" data-testid="feedback-section">
          {!feedbackGiven ? (
            <>
              <p className="text-sm text-muted-foreground mb-3">Was this article helpful?</p>
              <div className="flex justify-center gap-3 mb-3">
                <Button onClick={() => handleFeedback(true)} variant="outline" className="border-emerald-500/20 text-emerald-400 rounded-sm h-9" data-testid="feedback-up"><ThumbsUp className="w-4 h-4 mr-1" /> Yes</Button>
                <Button onClick={() => handleFeedback(false)} variant="outline" className="border-red-500/20 text-red-400 rounded-sm h-9" data-testid="feedback-down"><ThumbsDown className="w-4 h-4 mr-1" /> No</Button>
              </div>
              <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Optional comment..." className="max-w-sm mx-auto bg-black/30 border-white/10 rounded-sm text-xs" />
            </>
          ) : (
            <p className="text-sm text-emerald-400">Thank you for your feedback!</p>
          )}
        </div>

        {/* Related */}
        {article.related_articles?.length > 0 && (
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Related Articles</h3>
            <div className="space-y-1">
              {article.related_articles.map(r => (
                <Link key={r.slug} to={`/help/${r.slug}`} className="flex items-center justify-between p-3 bg-[#0a0a0a] border border-white/5 hover:border-emerald-500/20 group">
                  <div><p className="text-sm font-medium group-hover:text-emerald-400">{r.title}</p><p className="text-xs text-muted-foreground">{r.category}</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
